import React, {ReactElement, useContext} from "react";
import {ConfiguratorContext} from "../../context";
import {useQuoteContext} from "../../contexts/QuoteContext";
import useQuoteComments from "../../swr/useQuoteComments";
import {CommentStats, CommentTopic} from "../../api/models";
import {CommentOutlined, DollarCircleFilled, FileOutlined, MenuOutlined} from "@ant-design/icons";
import Title from "antd/lib/typography/Title";
import QuoteCommentList, {getDefaultCommentTopics} from "./QuoteCommentList";
import AssemblyDocumentList from "./AssemblyDocumentList";
import {Badge, Skeleton } from "antd";
import useQuoteReview from "../../swr/useQuoteReview";
import QuoteReviewDetail from "../QuoteReviewDetail";
import SidePanel from "../widgets/SidePanel";
import { Tab } from "rc-tabs/lib/interface";

const QuoteSidePanel = (props:{
    activeKey: string | undefined
    onChangeActiveKey: (k:string | undefined) => void
    PricingViewPanel: ReactElement | undefined
    isMobile: boolean
}) => {

    const configurator = useContext(ConfiguratorContext);

    const {quote} = useQuoteContext();

    const isAdminOrEngineering = configurator.isAdmin() || configurator.isEngineering();
    const isMobile = props.isMobile;

    const leftPanelIconStyle = { fontSize: '150%'};

    const quoteComments = useQuoteComments({
        quoteId: quote?.quoteId
    })

    const topicCommentStats = quoteComments.data
        ?.map( c => ({
            topic: c.topic,
            total: 1,
            unread: c.lastViewedAt ? 0 : 1,
        }))
        .reduce( ( acc, v ) => {
            const c = acc[ v.topic ];
            acc[ v.topic ] = {
                total: (c?.total || 0 ) + v.total,
                unread: (c?.unread || 0 ) + v.unread,
            };
            return acc;
        }, {});

    const getCommentTopicCount = (topic: CommentTopic | CommentTopic[]):CommentStats | undefined => {
        if ( !topicCommentStats ) return;

        return [topic].flat()
            .map( t => topicCommentStats[t] ).filter(v => v)
            .reduce( ( acc, v ) => ({
                total: acc.total + v.total,
                unread: acc.unread + v.unread,
            }), {total: 0, unread: 0});
    }

    const items : Tab[] = [];
    if ( props.PricingViewPanel) {
        items.push({
            key: "pricing-view",
            label: <>
                <DollarCircleFilled style={leftPanelIconStyle} />
                <Title level={5}>Pricing</Title>
            </>,
            children: props.PricingViewPanel
        });
    }


    items.push({
        key: "comments",
        label: <>
            {!!getCommentTopicCount(getDefaultCommentTopics(isAdminOrEngineering))?.total &&
              <div style={{position: "absolute", top: -10, right: 30}}>
                <Badge count={getCommentTopicCount(getDefaultCommentTopics(isAdminOrEngineering))?.total} size="small" color={getCommentTopicCount(getDefaultCommentTopics(isAdminOrEngineering))?.unread ? "red" : "grey" } />
              </div>}
            <CommentOutlined style={leftPanelIconStyle}  />
            <Title level={5}>Comments</Title>
        </>,
        children: <QuoteCommentList />
    });
    items.push({
        key: "documents",
        label: <>
            {!!getCommentTopicCount(CommentTopic.Documents)?.total &&
              <div style={{position: "absolute", top: -10, right: 30}}>
                <Badge count={getCommentTopicCount(CommentTopic.Documents)?.total} size="small" color={getCommentTopicCount(CommentTopic.Documents)?.unread ? "red" : "grey" } />
              </div>}
            <FileOutlined style={leftPanelIconStyle}  />
            <Title level={5}>Documents</Title>
        </>,
        children: <>
            <AssemblyDocumentList />
            <QuoteCommentList topics={[CommentTopic.Documents]} />
        </>
    });

    if ( isAdminOrEngineering ) {
        items.push( {
            key: "vfdReview",
            label: <>
                <MenuOutlined style={leftPanelIconStyle}  />
                <Title level={5}>VFD Review</Title>
            </>,
            children: <VfdReviewPanel />
        })
    }

    return <SidePanel
        activeKey={props.activeKey}
        items={items}
        isMobile={isMobile}
        onChange={props.onChangeActiveKey}
    />
}

export const VfdReviewPanel = (props:{
    includeAllCustomOptions?: boolean
}) => {

    const quoteContext = useQuoteContext();
    const quote = quoteContext.quote;
    const selectedOptions = quote?.selections;
    const selectedCustomOptions = quote?.selectedCustomOptions;

    const includeAllCustomOptions = props.includeAllCustomOptions ?? true;

    const review = useQuoteReview({
        quoteRevisionId: quote?.displayRevisionId,
        options: {
            dealerView: !quoteContext.adminView,
            selectedOptions: Object.values(selectedOptions || {}).flat(),
            selectedCustomOptions: Object.values(selectedCustomOptions || {}).flat(),
            includeAllCustomOptions
        }
    })

    return <Skeleton active loading={review.isLoading} >
        <QuoteReviewDetail review={review.data} />
    </Skeleton>
}

export default QuoteSidePanel;
